import { RefreshCw, Shield, University, Users } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { apiUrl } from '@/config/api';
import { useAuth } from '@/contexts/AuthContext';

const ROLES = [
  { label: 'System Admin', role: 'system_admin', icon: Shield },
  { label: 'Univ Admin', role: 'univ_admin', icon: University },
  { label: 'Dept Staff', role: 'dept_staff', icon: Users },
  { label: 'Applicant', role: 'applicant', icon: Users },
];

export function UserSwitcher() {
  const { user } = useAuth(); // We might need a raw "setSession" but AuthContext only has login.
  // Actually we need to call the dev login API and then manually update AuthContext state?
  // AuthContext `login` does fetch and sets state.
  // We should create a custom 'devLogin' function here or extend AuthContext.
  // Using `window.location.reload()` after setting localStorage is brute force but works for AuthContext initialization.
  // Better: AuthContext should expose `setSession` or we manually fetch and reload page.

  const [loading, setLoading] = useState(false);

  const handleSwitch = async (role: string) => {
    setLoading(true);
    try {
      const res = await fetch(apiUrl('/api/dev/login-as'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role }),
      });
      const data = (await res.json()) as {
        success: boolean;
        data: { user: unknown; accessToken: string };
        error?: string;
      };

      if (data.success) {
        // Manually set localStorage compatible with AuthContext
        localStorage.setItem(
          'admission-system-auth',
          JSON.stringify({
            user: data.data.user,
            accessToken: data.data.accessToken,
          }),
        );
        // Force reload to pick up new session
        window.location.reload();
      } else {
        alert(`Failed to switch user: ${data.error}`);
      }
    } catch (e) {
      console.error(e);
      alert('Error switching user');
    } finally {
      setLoading(false);
    }
  };

  const isDev = import.meta.env.DEV;
  const isDemo = import.meta.env.VITE_DEMO_MODE === 'true';

  if (!isDev && !isDemo) return null;

  return (
    <div className="fixed bottom-4 right-16 z-[9999]">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="bg-slate-900 text-white border-slate-700 shadow-lg hover:bg-slate-800 h-9 px-3 gap-2 rounded-full"
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4 text-blue-400" />}
            <span className="text-xs font-mono">{user ? user.role : 'Guest'}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56 bg-slate-900 border-slate-700 text-slate-300">
          <DropdownMenuLabel className="text-slate-500 text-xs uppercase tracking-wider">
            Dev User Switcher
          </DropdownMenuLabel>
          <DropdownMenuSeparator className="bg-slate-800" />
          {ROLES.map((role) => (
            <DropdownMenuItem
              key={role.role}
              onClick={() => handleSwitch(role.role)}
              disabled={loading || user?.role === role.role}
              className="focus:bg-slate-800 focus:text-white cursor-pointer gap-2"
            >
              <role.icon className="w-4 h-4" />
              <span>{role.label}</span>
              {user?.role === role.role && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-green-500" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
